import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, Wrench, IndianRupee, CalendarRange, Headphones, ArrowRight, Search, CalendarCheck, Car, Undo2, UserRound, Plane, Building2, Quote, TicketPercent, Star } from "lucide-react";
import { publicApi } from "@/lib/api";
import { BRAND, CAR_TYPES, inr } from "@/lib/brand";
import { SearchBar } from "@/components/site/SearchBar";
import { GetApp } from "@/components/site/GetApp";
import { CarCard, savingsText } from "@/components/site/CarCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const trust = [[ShieldCheck, "Verified vehicles", "Every car document-checked"], [Wrench, "Inspected & maintained", "Sanitised before every trip"], [IndianRupee, "Transparent pricing", "No hidden charges"], [CalendarRange, "Flexible rentals", "Hourly, daily & weekly"], [Headphones, "24/7 support", "We're always a call away"]];
const steps = [[Search, "Search & choose", "Pick dates and find the right car."], [CalendarCheck, "Verify & book", "Upload licence, pay securely online."], [Car, "Pick up & drive", "Collect at the hub or get it delivered."], [Undo2, "Return easily", "Drop it back, deposit refunded."]];
const services = [[Car, "Self-drive rentals", "Drive yourself, by the hour or day.", "/cars"], [UserRound, "Chauffeur-driven", "A verified professional driver.", "/cars?driver=1"], [Plane, "Airport transfers", "Pickup & drop at BLR airport.", "/cars?driver=1&airport=1"], [Building2, "Corporate mobility", "GST invoicing for your team.", "/business"]];
const faqs = [
  ["What do I need to rent a car?", "A valid driving licence held for at least 1 year, Aadhaar, and a live selfie. Upload them once — our team verifies them before your first trip."],
  ["How is the price calculated?", "You pay an hourly rate (minimum 4 hours) plus a ₹99 convenience fee and 18% GST. Longer trips get automatic discounts. A refundable security deposit is collected with the booking."],
  ["Can I cancel my booking?", "Yes. Cancel 48+ hours before pickup for a full refund of charges, 24–48 hours for 50%. Your security deposit is always refunded."],
  ["Is fuel included?", "No — return the car with the same fuel or charge level as at pickup. Kilometres included per 24 hours are shown on every car."],
];
const FLEET_IMG = { Hatchback: "hatchback", Sedan: "sedan", SUV: "suv", MUV: "muv", "Off-road": "offroad" };

function Hero({ cities }) {
  return (
    <section className="relative" data-testid="home-hero">
      <div className="relative overflow-hidden bg-hc-deep">
        <img src="/img/hero-hc.jpg" alt="White SUV in front of a Bengaluru landmark at golden hour" className="absolute inset-0 h-full w-full object-cover object-[70%_60%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2E1D]/95 via-[#0B3D26]/70 to-transparent" />
        <div className="container-x relative pb-40 pt-12 sm:pt-16 lg:pb-44 lg:pt-20">
          <p className="fade-up text-xs font-semibold uppercase tracking-[0.12em] text-white/90 sm:tracking-[0.22em]">Self-drive car rentals in <span className="text-hc-gold">{BRAND.city}</span></p>
          <h1 className="fade-up mt-4 max-w-2xl font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            Your City. <span className="text-hc-gold">Your Drive.</span><br />On Your Terms.
          </h1>
          <p className="fade-up mt-5 max-w-md text-base leading-7 text-white/85" style={{ animationDelay: "160ms" }}>Clean, inspected cars. Verified drivers. Transparent pricing.</p>
          <div className="fade-up mt-6 flex flex-wrap gap-x-7 gap-y-3" style={{ animationDelay: "220ms" }}>
            {[[ShieldCheck, "Verified Cars"], [ShieldCheck, "Trip Protection"], [Headphones, "24×7 Support"]].map(([I, t]) => (
              <span key={t} className="flex items-center gap-2 text-sm font-semibold text-white"><I className="h-5 w-5 text-hc-gold" />{t}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="container-x relative z-10 -mt-28 fade-up sm:-mt-32" style={{ animationDelay: "280ms" }}>
        <SearchBar cities={cities} />
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section data-testid="home-features">
      <div className="container-x grid grid-cols-2 gap-6 py-9 sm:grid-cols-3 lg:grid-cols-5">
        {trust.map(([I, t, d]) => (
          <div key={t} className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-hc-mint"><I className="h-5 w-5 text-hc-green" /></span>
            <div className="min-w-0"><p className="text-sm font-semibold text-hc-ink">{t}</p><p className="text-xs text-hc-slate">{d}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHead({ title, sub, link, linkLabel, testId }) {
  return (
    <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h2 className="font-display text-3xl font-extrabold tracking-tight text-hc-ink sm:text-4xl">{title}</h2>
        {sub && <p className="mt-2 text-hc-slate">{sub}</p>}
      </div>
      {link && <Link to={link} data-testid={testId} className="group flex items-center gap-1.5 text-sm font-semibold text-hc-green">{linkLabel}<ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></Link>}
    </div>
  );
}

function Fleet({ cars }) {
  const minByType = useMemo(() => Object.fromEntries(CAR_TYPES.map((t) => {
    const list = cars.filter((c) => c.type === t.type);
    return [t.type, list.length ? Math.min(...list.map((c) => c.price_per_hour)) : null];
  })), [cars]);
  return (
    <section className="bg-white" data-testid="home-fleet">
      <div className="container-x py-14">
        <SectionHead title="Explore Our Fleet" sub="From city hatchbacks to 7-seaters and 4x4s." link="/cars" linkLabel="View All Cars" testId="home-view-all" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
          {CAR_TYPES.map((t, i) => (
            <Link key={t.type} to={`/cars?type=${t.type}`} data-testid={`home-type-${t.type}`} style={{ animationDelay: `${i * 70}ms` }}
              className="fade-up group overflow-hidden rounded-2xl border border-hc-sage bg-hc-cream transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(11,61,38,0.35)]">
              <div className="aspect-[16/10] overflow-hidden"><img src={`/img/fleet-${FLEET_IMG[t.type]}.jpg`} alt={t.label} loading="lazy" className="h-full w-full object-cover object-[center_60%] transition-transform duration-500 group-hover:scale-105" /></div>
              <div className="px-4 py-3.5 text-center">
                <p className="text-[15px] font-bold text-hc-ink">{t.label}</p>
                <p className="tabular text-sm text-hc-slate" data-testid={`home-type-price-${t.type}`}>{minByType[t.type] ? `From ${inr(minByType[t.type])}/hr` : "Coming soon"}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Popular({ cars, savings }) {
  if (!cars.length) return null;
  return (
    <section data-testid="home-popular">
      <div className="container-x py-14">
        <SectionHead title="Popular Cars" sub="Most booked by drivers in Bengaluru." link="/cars?sort=popular" linkLabel="See more" testId="home-popular-more" />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {cars.slice(0, 4).map((c, i) => <CarCard key={c.id} car={c} index={i} savings={savings} />)}
        </div>
      </div>
    </section>
  );
}

function Offers({ offers, tiers }) {
  const top = [...tiers].sort((a, b) => b.pct - a.pct)[0];
  return (
    <section data-testid="home-offers">
      <div className="container-x grid gap-4 pb-14 md:grid-cols-2">
        {top && (
          <div className="relative overflow-hidden rounded-2xl bg-hc-green p-7 text-white">
            <p className="label-eyebrow !text-hc-gold">Long trip savings</p>
            <p className="mt-2 font-display text-3xl font-extrabold">Up to {top.pct}% off</p>
            <p className="mt-1 text-white/80">{savingsText(tiers)} — applied automatically.</p>
            <Link to="/cars" className="btn-gold mt-5" data-testid="home-offer-browse">Browse cars</Link>
          </div>
        )}
        <div className="card-hc p-7" data-testid="home-coupons">
          <p className="label-eyebrow">Offers & coupons</p>
          {offers.length === 0 ? <p className="mt-3 text-hc-slate">No coupon running right now — check back soon or refer a friend for ₹300 off.</p> : (
            <ul className="mt-3 space-y-3">
              {offers.slice(0, 3).map((o) => (
                <li key={o.code} className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-hc-gold bg-hc-cream px-4 py-3" data-testid={`home-coupon-${o.code}`}>
                  <span className="flex items-center gap-2 text-sm font-semibold text-hc-ink"><TicketPercent className="h-4 w-4 text-hc-goldh" />{o.kind === "flat" ? `${inr(o.value)} off` : `${o.value}% off`}{o.first_trip_only ? " · first trip" : ""}</span>
                  <code className="rounded-lg bg-hc-green px-2.5 py-1 text-xs font-bold tracking-wider text-white">{o.code}</code>
                </li>
              ))}
            </ul>
          )}
          <Link to="/offers" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-hc-green" data-testid="home-offers-all">All offers <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how-it-works" className="scroll-mt-24 bg-white" data-testid="home-how">
      <div className="container-x py-16">
        <SectionHead title="How HooreCars Works" sub="Book, drive and return — it's that simple." />
        <div className="relative mt-4 grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4">
          <div className="absolute left-[12.5%] right-[12.5%] top-7 hidden border-t-2 border-dashed border-hc-sage md:block" />
          {steps.map(([I, t, d], i) => (
            <div key={t} className="relative text-center" data-testid={`how-step-${i + 1}`}>
              <span className="relative mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-hc-mint ring-[10px] ring-white">
                <I className="h-7 w-7 text-hc-green" strokeWidth={1.9} />
                <span className="absolute -right-2.5 -top-2.5 grid h-6 w-6 place-items-center rounded-full bg-hc-gold text-xs font-bold text-hc-ink ring-2 ring-white">{i + 1}</span>
              </span>
              <h3 className="mt-5 text-lg font-bold text-hc-ink">{t}</h3>
              <p className="mx-auto mt-1 max-w-[190px] text-sm leading-5 text-hc-slate">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section data-testid="home-services">
      <div className="container-x py-14">
        <SectionHead title="One platform, every kind of trip" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map(([I, t, d, to]) => (
            <Link key={t} to={to} className="card-hc group p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`home-service-${t.split(" ")[0].toLowerCase()}`}>
              <I className="h-8 w-8 text-hc-green" strokeWidth={1.7} />
              <p className="mt-4 font-display text-lg font-bold text-hc-ink">{t}</p>
              <p className="mt-1 text-sm text-hc-slate">{d}</p>
              <ArrowRight className="mt-4 h-4 w-4 text-hc-goldh transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HostBanner() {
  return (
    <section className="px-3 pb-5 sm:px-5" data-testid="home-host-banner">
      <div className="relative mx-auto max-w-[1600px] overflow-hidden rounded-3xl bg-hc-deep">
        <img src="/img/host.jpg" alt="Thumbs up from a car window" className="absolute inset-0 h-full w-full object-cover object-left opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-hc-deep/80 to-hc-deep" />
        <div className="relative flex min-h-[220px] flex-col justify-center gap-5 px-6 py-10 sm:px-10 md:ml-[40%] md:pl-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-display text-2xl font-extrabold leading-tight text-white sm:text-3xl">Host your car & earn<br />with <span className="text-hc-gold">HooreCars</span></h2>
            <p className="mt-3 max-w-md text-[15px] leading-6 text-white/85">List your car, we handle bookings, verified drivers, cleaning and support.</p>
          </div>
          <Link to="/host" data-testid="home-start-hosting" className="btn-gold shrink-0 !px-7 !py-3.5">Start Hosting <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </section>
  );
}

function Testimonials({ reviews }) {
  if (!reviews.length) return null;
  return (
    <section className="bg-white" data-testid="home-testimonials">
      <div className="container-x py-14">
        <SectionHead title="Loved by our drivers" sub="Verified reviews from completed trips." />
        <div className="grid gap-4 md:grid-cols-3">
          {reviews.slice(0, 3).map((r) => (
            <figure key={r.id} className="rounded-2xl border border-hc-sage bg-hc-cream p-6" data-testid={`testimonial-${r.id}`}>
              <Quote className="h-6 w-6 text-hc-gold" />
              <blockquote className="mt-3 text-[15px] leading-6 text-hc-ink">{r.comment}</blockquote>
              <figcaption className="mt-4 flex items-center justify-between text-sm"><span className="font-semibold">{r.name} · <span className="font-normal text-hc-slate">{r.car_name}</span></span>
                <span className="flex items-center gap-1 text-hc-goldh"><Star className="h-4 w-4 fill-current" />{r.rating}</span></figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeFaq() {
  return (
    <section data-testid="home-faq">
      <div className="container-x grid gap-8 py-14 lg:grid-cols-[1fr_1.6fr]">
        <div>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-hc-ink sm:text-4xl">Questions? Answered.</h2>
          <p className="mt-2 text-hc-slate">Everything you need to know before your first drive.</p>
          <Link to="/faq" className="btn-outline mt-5" data-testid="home-faq-all">All FAQs</Link>
        </div>
        <Accordion type="single" collapsible className="card-hc px-5">
          {faqs.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} className="border-hc-sage last:border-0">
              <AccordionTrigger className="text-left font-semibold text-hc-ink hover:no-underline" data-testid={`home-faq-q${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-hc-slate">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export default function Home() {
  const [cars, setCars] = useState([]);
  const [cities, setCities] = useState([]);
  const [offers, setOffers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { hash } = useLocation();
  useEffect(() => {
    publicApi.get("/cars", { params: { sort: "popular" } }).then((r) => setCars(r.data)).catch(() => {});
    publicApi.get("/cars/meta").then((r) => setCities(r.data.city)).catch(() => {});
    publicApi.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
    publicApi.get("/pricing").then((r) => setTiers(r.data.duration_discounts || [])).catch(() => {});
    publicApi.get("/reviews/featured").then((r) => setReviews(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [hash]);
  return (
    <div data-testid="home-page">
      <Hero cities={cities} />
      <TrustStrip />
      <Fleet cars={cars} />
      <Popular cars={cars} savings={savingsText(tiers)} />
      <Offers offers={offers} tiers={tiers} />
      <HowItWorks />
      <Services />
      <HostBanner />
      <Testimonials reviews={reviews} />
      <GetApp />
      <HomeFaq />
    </div>
  );
}
